import React from 'react'
import dayjs from 'dayjs'
import { DateRange, DayPicker } from 'react-day-picker'
import { ru } from 'react-day-picker/locale'

import { Button } from '../button'
import { Flex } from '../flex'
import { IconChevronLeft } from '../icon/icon-chevron-left'
import { Input } from '../input'
import { Modal } from '../modal'
import styles from './calendar-range.module.scss'
import { DATE_FORMAT } from './constants'
import { useCalendarState } from './hooks/use-calendar-state'

interface CalendarModalProps {
    value?: DateRange
    isOpen: boolean
    onClose: () => void
    onChange: (value?: DateRange) => void
    getInjectedEl?: () => HTMLElement
    hasMaxDate?: boolean
}

const TODAY_DATE = new Date()

export const CalendarModal: React.FC<CalendarModalProps> = ({
    isOpen,
    value,
    onChange,
    onClose,
    getInjectedEl,
    hasMaxDate,
}) => {
    const { selected, month, setSelected, setMonth, handleMonthChange } = useCalendarState(value)

    const handleApply = React.useCallback(() => {
        onChange(selected)
        onClose()
    }, [onChange, onClose, selected])

    const handleReset = React.useCallback(() => {
        setSelected(undefined)
        setMonth(TODAY_DATE)
        onChange()
    }, [onChange, setMonth, setSelected])

    return (
        <Modal
            inject={getInjectedEl && getInjectedEl()}
            isOpen={isOpen}
            size='small'
            onClose={onClose}
        >
            <Modal.Body isFitContent>
                <div className={styles['calendar__wrapper']}>
                    <div className={styles['calendar__inputs']}>
                        <Flex grow={1} spacing='m' wrap='nowrap'>
                            <Input
                                label='От'
                                type='button'
                                value={selected?.from && dayjs(selected.from).format(DATE_FORMAT)}
                                onClick={() => setMonth(selected?.from || TODAY_DATE)}
                            />
                            <Input
                                label='До'
                                type='button'
                                value={selected?.to && dayjs(selected.to).format(DATE_FORMAT)}
                                onClick={() => setMonth(selected?.to || TODAY_DATE)}
                            />
                        </Flex>
                    </div>
                    <div className={styles['calendar__picker']}>
                        <DayPicker
                            locale={ru}
                            mode='range'
                            month={month}
                            numberOfMonths={1}
                            selected={selected}
                            showOutsideDays={false}
                            components={{
                                Chevron: () => <IconChevronLeft size={20} />,
                            }}
                            disabled={
                                hasMaxDate
                                    ? {
                                          after: TODAY_DATE,
                                      }
                                    : undefined
                            }
                            onNextClick={() => handleMonthChange('next')}
                            onPrevClick={() => handleMonthChange('prev')}
                            onSelect={setSelected}
                        />
                    </div>
                    <Flex justify='flex-end' spacing='xs'>
                        <Button size='small' variant='secondary' onClick={handleReset}>
                            Сбросить
                        </Button>
                        <Button size='small' variant='primary' onClick={handleApply}>
                            Применить
                        </Button>
                    </Flex>
                </div>
            </Modal.Body>
        </Modal>
    )
}
